import { PublicMeeting } from '@chilipiper/api-client/src/meetings'
import { useCallback } from 'react'
import invariant from 'tiny-invariant'
import { PublicMeetingType } from '@chilipiper/api-client/src/meeting-types'
import { FairlyPrioritizedUsers } from '@chilipiper/api-client/src/distribution'
import { useMeetingSettings, useRescheduleMeeting } from '../api-queries'
import { SelectTimeSlotParams } from '../../load-availability-options/types'
import {
  hasFlexibleReassignment,
  parseRescheduleAssignmentsToScheduleByDistributionAssignees,
} from '../helpers'
import {
  buildReserveTimeSlotPayload,
  getMeetingLimit,
  getLinkInfo,
  getLinkInformationFromScheduleOrigin,
} from '../../load-availability-options'
import { getRecheduleReservationUsers } from '../helpers/reservation-users'
import { AvailabilityOptions } from '../../load-availability-options'
import {
  useSchedulingLink,
  useGroupSchedulingLinkById,
} from '../../load-availability-options/api-queries/scheduling-link'
import { useReservationSlot } from '../../reserve-slot/api-queries'
import { useMeetingTypeOverride } from './use-meeting-type-override'

export const useReschedule = (
  tenantId: string,
  availabilityOptions?: AvailabilityOptions,
  meetingType?: PublicMeetingType,
  meetingDetails?: PublicMeeting,
  distributions?: Record<string, FairlyPrioritizedUsers>
) => {
  const {
    mutateAsync: onRescheduleMeeting,
    isPending: isLoadingRescheduleMeeting,
    error: rescheduleMeetingError,
  } = useRescheduleMeeting()
  const {
    onReserveTimeSlot,
    isLoadingReservation: isLoadingTimeSlotReservation,
    error: reserveTimeSlotError,
  } = useReservationSlot()

  const { data: meetingSettings } = useMeetingSettings({
    tenantId,
    meetingId: meetingDetails?.meetingId,
  })
  const meetingTypeOverride = useMeetingTypeOverride({
    tenantId,
    meetingDetails,
    meetingId: meetingDetails?.meetingId,
  })
  const linkInformation = getLinkInformationFromScheduleOrigin(meetingDetails?.scheduleOrigin)

  const { data: schedulingLink } = useSchedulingLink({
    tenantId,
    schedulingLinkId: linkInformation?.id,
    enabled: linkInformation?.type === 'AdminSchedulingLink',
  })
  const { data: groupSchedulingLink } = useGroupSchedulingLinkById({
    tenantId,
    groupLinkId: linkInformation?.id,
    enabled: linkInformation?.type === 'GroupSchedulingLink',
  })

  const hostMeetingLimit = getMeetingLimit({
    linkInfo: getLinkInfo({
      type: linkInformation?.type,
      groupSchedulingLink: groupSchedulingLink,
      meetingType,
      adminSchedulingLink: schedulingLink,
    }),
    type: linkInformation?.type,
    id: linkInformation?.id,
  })

  const rescheduleMeeting = useCallback(
    async ({ slot, userIds, availableRoundRobinAssignees }: SelectTimeSlotParams) => {
      const leaveAsIs = {
        type: 'LeaveAsIs',
      } as const

      invariant(meetingDetails, 'Meeting details not found')

      if (
        meetingDetails.assignments?.some(assignment =>
          hasFlexibleReassignment({ assignment, distributions: distributions ?? {} })
        )
      ) {
        return onRescheduleMeeting({
          tenantId,
          meetingId: meetingDetails.meetingId,
          payload: {
            attendees: leaveAsIs,
            location: leaveAsIs,
            meetingTypeOverride: leaveAsIs,
            noShowStatus: leaveAsIs,
            timeAndAssignment: {
              type: 'Replace',
              value: {
                type: 'WithUnresolvedAssignees',
                assignments: {
                  type: 'Replace',
                  value: parseRescheduleAssignmentsToScheduleByDistributionAssignees(
                    meetingDetails.assignments ?? [],
                    availableRoundRobinAssignees
                  ),
                },
                meetingTypeReference: {
                  type: 'LeaveAsIs',
                },
                interval: {
                  startsAt: slot,
                  duration: meetingTypeOverride?.duration,
                },
              },
            },
            origin: {
              type: 'RescheduleLink',
            },
          },
        })
      }

      const additionalRequiredAttendees = availabilityOptions?.additionalRequiredAttendees || []
      const ownershipAndUsersAttendees =
        availabilityOptions && 'ownershipAndUsersAttendees' in availabilityOptions
          ? availabilityOptions.ownershipAndUsersAttendees
          : []

      const reservePayload = buildReserveTimeSlotPayload({
        users: getRecheduleReservationUsers({
          availableRoundRobinAssignees,
          userIds,
          meetingLimit: hostMeetingLimit,
          additionalAttendees: [...additionalRequiredAttendees, ...ownershipAndUsersAttendees],
        }),
        slotStartsAt: slot,
        meetingTypeOverride,
        meetingTypeRef: {
          id: meetingSettings?.meetingType.reference.meetingTypeId as string,
          timestamp: meetingSettings?.meetingType.reference.timestamp as string,
        },
      })

      return onReserveTimeSlot({
        tenantId,
        payload: reservePayload,
        feature: meetingSettings?.priority === 'High' ? 'highPriorityReservation' : undefined,
      }).then(async reservation => {
        invariant(reservation, 'Reservation not found')
        invariant(meetingDetails, 'Meeting details not found')

        return onRescheduleMeeting({
          tenantId,
          meetingId: meetingDetails.meetingId,
          payload: {
            attendees: leaveAsIs,
            location: leaveAsIs,
            meetingTypeOverride: leaveAsIs,
            noShowStatus: leaveAsIs,
            timeAndAssignment: {
              type: 'Replace',
              value: {
                type: 'WithReservationId',
                reservationId: reservation.id,
                assignment: leaveAsIs,
                hostId: leaveAsIs,
              },
            },
            origin: {
              type: 'RescheduleLink',
            },
          },
        })
      })
    },
    [tenantId, meetingDetails, meetingTypeOverride, meetingSettings, availabilityOptions]
  )

  return {
    rescheduleMeeting,
    isLoading: isLoadingTimeSlotReservation || isLoadingRescheduleMeeting,
    error: reserveTimeSlotError || rescheduleMeetingError,
  }
}
